// API Security · Discovery — endpoint detail side drawer (v3 shell).



import React from "react";

import {

  Stack, Box, Typography, Button, Chip,

} from "@mui/material";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import RouteIcon from "@mui/icons-material/AltRoute";

import copy from "copy-to-clipboard";

import colors from "../../../../../../utils/colors";

import SectionHelp from "./SectionHelp";

import { primaryButtonSx } from "../../../../../common/Button";

import { DETECTION_ICONS } from "../../../../../../icons/detectionLucideIcons";

import { formatDate } from "../../../../../../utils/format";

import WafEventDrawerShell from "../../../detection/WafEventDrawerShell";

import FindingDrawerHeader from "../../../detection/v3/FindingDrawerHeader";

import SmartParseRawJsonViewer from "../../../detection/v3/SmartParseRawJsonViewer";

import {

  SmartParseSectionCard as SectionCard,

  DrawerFieldStack,

  spNetworkFieldValueSx,

} from "../../../detection/v3/smartParseDrawerUi";

import {

  TEXT_PRIMARY,

  TEXT_SECONDARY,

  ERROR_COLOR,

  secondaryButtonStyle,

  tagChipSx,

} from "../../../detection/wafEventDrawerShared";



function SpecCoverageChip({ coverage }) {

  const meta = {

    documented: { label: "Documented", bg: "rgba(16, 185, 129, 0.10)", fg: "#0f766e" },

    shadow:     { label: "Shadow",     bg: "rgba(245, 158, 11, 0.10)", fg: "#b45309" },

    mismatch:   { label: "Mismatch",   bg: "rgba(99, 113, 247, 0.10)", fg: "#4338ca" },

    no_spec:    { label: "No spec",    bg: "#F3F4F6", fg: TEXT_SECONDARY },

  }[coverage] || { label: "—", bg: "#F3F4F6", fg: TEXT_SECONDARY };

  return (

    <Chip label={meta.label} size="small" sx={{ ...tagChipSx, backgroundColor: meta.bg, color: meta.fg }} />

  );

}



function sectionHelp(tooltip) {

  return tooltip ? (

    <SectionHelp tooltip={tooltip} size="small" placement="left" />

  ) : null;

}



function renderChipFieldContent(items, chipSx) {

  if (!(items || []).length) {

    return <Typography sx={spNetworkFieldValueSx}>—</Typography>;

  }

  return (

    <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>

      {(items || []).map((k) => (

        <Chip key={k} label={k} size="small" sx={{ fontSize: 12, height: 22, ...chipSx }} />

      ))}

    </Stack>

  );

}



export default function EndpointDetailDrawer({

  open, onClose, row, driftRow, candidateEndpointId, hasActiveSpec, closedWorld,

  onMarkReviewed, onIgnore, onPromote,

  canConfigure = true,

  canWrite = true,

}) {

  const [snackOpen, setSnackOpen] = React.useState(false);



  const handleCopy = (value) => {

    if (!value || value === "—") return;

    copy(String(value));

    setSnackOpen(true);

    setTimeout(() => setSnackOpen(false), 2000);

  };



  if (!row) {

    return (

      <WafEventDrawerShell open={false} onClose={onClose} title="API Endpoint">

        {null}

      </WafEventDrawerShell>

    );

  }



  const isShadow = row._coverage === "shadow";

  const isMismatch = row._coverage === "mismatch";

  const isDocumented = row._coverage === "documented";

  const willBe404d = isShadow && row.review_state === "saved" && closedWorld;

  const driftDetail = driftRow?.detail || {};

  const undeclared = Array.isArray(driftDetail.undeclared_query_keys)

    ? driftDetail.undeclared_query_keys : [];



  const rawPayload = {

    endpoint: row,

    drift: driftRow || null,

    candidate_endpoint_id: candidateEndpointId || null,

    has_active_spec: hasActiveSpec,

    closed_world: closedWorld,

  };



  return (

    <>

      <WafEventDrawerShell

        open={open}

        onClose={onClose}

        title="API Endpoint"

        headerVariant="hero"

        headerContent={(

          <FindingDrawerHeader

            embedded

            title="API Endpoint"

            method={row.method}

            path={row.path_template}

            detailLine={row.host}

            icon={RouteIcon}

            idLabel="ID"

            idValue={row.id}

            onCopyId={handleCopy}

          />

        )}

      >

        {willBe404d && (

          <Box sx={{

            p: 1.5,

            borderRadius: "8px",

            border: "1px solid rgba(220, 38, 38, 0.30)",

            backgroundColor: "rgba(220, 38, 38, 0.06)",

          }}>

            <Stack direction="row" alignItems="center" spacing={1}>

              <WarningAmberIcon sx={{ fontSize: 16, color: ERROR_COLOR }} />

              <Typography sx={{ fontSize: 14, color: ERROR_COLOR, fontWeight: 600 }}>

                Currently being 404-ed

              </Typography>

            </Stack>

            <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 0.75, lineHeight: "18px" }}>

              Closed-world is enabled. This endpoint has been marked reviewed but isn&apos;t in the active spec,

              so Layerd is rejecting real traffic for it. Add it to the spec below to unblock.

            </Typography>

          </Box>

        )}



        <SectionCard

          icon={DETECTION_ICONS.request}

          title="Identity"

          headerExtra={sectionHelp("What the catalog observed about this endpoint, derived from real traffic. Path template collapses variable segments (/users/123 → /users/{id}).")}

        >

          <DrawerFieldStack

            columns={2}

            onCopy={handleCopy}

            fields={[

              { key: "host", label: "Host", value: row.host, copyable: true },

              { key: "method", label: "Method", value: row.method },

              { key: "classification", label: "Classification", value: row.classification },

              { key: "pathTemplate", label: "Path template", value: row.path_template, copyable: true },

              { key: "owner", label: "Owner", value: row.owner_team },

              {

                key: "queryKeys",

                label: "Query keys",

                fullRow: true,

                content: renderChipFieldContent(row.query_keys, { backgroundColor: "#F9FAFB" }),

              },

              {

                key: "sensitivityTags",

                label: "Sensitivity tags",

                fullRow: true,

                content: renderChipFieldContent(row.sensitivity_tags, { backgroundColor: colors.neutral[200] }),

              },

            ]}

          />

        </SectionCard>



        <SectionCard

          icon={DETECTION_ICONS.detection}

          title="Status"

          headerExtra={sectionHelp("Review state is the operator-curation flag (needs review / reviewed / ignored). Lifecycle status is derived automatically from traffic recency and spec linkage by the status_classifier cron.")}

        >

          <DrawerFieldStack

            columns={2}

            fields={[

              { key: "reviewState", label: "Review state", value: (row.review_state || "needs_review").replace(/_/g, " ") },

              { key: "reviewer", label: "Reviewer", value: row.reviewed_by },

              { key: "reviewedAt", label: "Reviewed at", value: formatDate(row.reviewed_at) },

              { key: "lifecycle", label: "Lifecycle status", value: row.status },

              { key: "lastSeen", label: "Last seen", value: formatDate(row.last_seen) },

              {

                key: "observations",

                label: "Observations (30d)",

                value: typeof row.observation_count_30d === "number"

                  ? row.observation_count_30d.toLocaleString()

                  : row.observation_count_30d,

              },

            ]}

          />

        </SectionCard>



        {hasActiveSpec && (

          <SectionCard

            icon={DETECTION_ICONS.info}

            title="Spec coverage"

            headerExtra={sectionHelp("Where this endpoint sits relative to the active OpenAPI spec. Documented = matched; Shadow = observed but not in spec; Mismatch = path documented but query keys were observed that the spec doesn't declare.")}

          >

            <DrawerFieldStack

              columns={2}

              onCopy={handleCopy}

              fields={[

                {

                  key: "coverage",

                  label: "Coverage",

                  content: <SpecCoverageChip coverage={row._coverage} />,

                },

                isDocumented

                  ? { key: "specId", label: "Active spec id", value: row.spec_id, copyable: true }

                  : null,

                (isShadow || isMismatch) && driftRow

                  ? { key: "driftDetected", label: "Drift detected", value: formatDate(driftRow.detected_at) }

                  : null,

                (isShadow || isMismatch) && driftRow

                  ? { key: "driftSeverity", label: "Severity", value: driftRow.severity }

                  : null,

                isMismatch && undeclared.length > 0

                  ? {

                    key: "undeclaredKeys",

                    label: "Undeclared keys",

                    fullRow: true,

                    content: renderChipFieldContent(undeclared, {

                      backgroundColor: "rgba(99, 113, 247, 0.10)",

                      color: "#4338ca",

                    }),

                  }

                  : null,

              ]}

            />

          </SectionCard>

        )}



        {isShadow && candidateEndpointId && canWrite && (

          <SectionCard

            icon={DETECTION_ICONS.detection}

            title="Pending candidate"

            headerExtra={sectionHelp("The spec_synthesizer cron drafted a candidate operation for this endpoint from observed traffic. Clicking Add to spec grafts just this one candidate into the active spec — see Schema Validation to review the whole draft.")}

          >

            <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>

              A synthesized draft is ready for this endpoint.

            </Typography>

            <Button

              variant="contained"

              disableElevation

              startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}

              onClick={() => onPromote?.(candidateEndpointId, row.host, row.method, row.path_template)}

              sx={{ ...primaryButtonSx, width: "fit-content" }}

            >

              Add to spec

            </Button>

          </SectionCard>

        )}



        {canConfigure && (

          <SectionCard

            icon={DETECTION_ICONS.request}

            title="Actions"

            headerExtra={sectionHelp("Mark reviewed = curation flag (does not change enforcement). Ignore = excludes from drift detection + candidate spec going forward. Add to spec = grafts a candidate endpoint into the active spec; Layerd reloads in ~2s.")}

          >

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>

              <Button variant="outlined" onClick={() => onMarkReviewed?.([row.id])} sx={secondaryButtonStyle}>

                Mark reviewed

              </Button>

              <Button variant="outlined" onClick={() => onIgnore?.([row.id])} sx={secondaryButtonStyle}>

                Ignore

              </Button>

            </Stack>

          </SectionCard>

        )}



        <SectionCard icon={DETECTION_ICONS.info} title="Raw data" defaultExpanded={false}>

          <SmartParseRawJsonViewer

            data={rawPayload}

            onCopy={handleCopy}

            filename="endpoint_detail.json"

          />

        </SectionCard>

      </WafEventDrawerShell>



      {snackOpen && (

        <Box

          sx={{

            position: "fixed",

            bottom: 80,

            right: 24,

            backgroundColor: TEXT_PRIMARY,

            color: "#fff",

            px: 2,

            py: 1,

            borderRadius: "8px",

            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",

            zIndex: 1400,

          }}

        >

          <Typography sx={{ fontSize: 14, color: "#fff" }}>

            Copied to clipboard!

          </Typography>

        </Box>

      )}

    </>

  );

}

